import type { Rect, UiRects } from "../../game/input";
import type { InboxMessage } from "../../social/messageService";
import type { RenderModel } from "../renderModel";
import { GAME_FONT, type PhoneMetrics } from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface PhoneMessageDetailDeps {
  formatTimeAgo: (iso: string) => string;
  wrapTextToWidth: (text: string, maxWidth: number, maxLines: number) => string[];
}

// ── Extracted screen function ───────────────────────────────────────

export function drawPhoneMessageDetailScreen(
  deps: PhoneMessageDetailDeps,
  ctx: CanvasRenderingContext2D,
  _model: RenderModel,
  msg: InboxMessage,
  scrX: number, scrY: number, scrW: number, scrH: number,
  contentX: number, contentW: number, contentRight: number,
  metrics: PhoneMetrics,
  uiRects: UiRects,
): void {
  const desktop = metrics.desktop;
  let curY = scrY + metrics.topPad;

  // Header
  ctx.fillStyle = "#64d8ff";
  ctx.font = `bold ${metrics.titleFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("MESSAGE", scrX + scrW / 2, curY);
  curY += metrics.titleGap;

  // Divider
  ctx.fillStyle = "#2a3a55";
  for (let dx = contentX; dx < contentRight; dx += 4) {
    ctx.fillRect(dx, curY, 2, 1);
  }
  curY += metrics.sectionGap;

  // Sender info
  const avatarSize = desktop ? 28 : 22;
  ctx.fillStyle = msg.accentColor;
  ctx.fillRect(contentX, curY, avatarSize, avatarSize);
  ctx.fillStyle = "#0c1624";
  ctx.font = `bold ${desktop ? 12 : 9}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(msg.senderName.charAt(0), contentX + avatarSize / 2, curY + avatarSize / 2);

  ctx.fillStyle = msg.accentColor;
  ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(msg.senderName, contentX + avatarSize + 8, curY + 2);

  ctx.fillStyle = "#5a7a9a";
  ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
  ctx.fillText(deps.formatTimeAgo(msg.createdAt), contentX + avatarSize + 8, curY + (desktop ? 14 : 12));
  curY += avatarSize + metrics.sectionGap;

  // Body text (word-wrapped)
  ctx.fillStyle = "#c8d8f0";
  ctx.font = `${metrics.metaFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  const bodyMaxW = contentW - 4;
  const bodyLines = deps.wrapTextToWidth(msg.body, bodyMaxW, 8);
  const bodyLineH = desktop ? 10 : 8;
  for (const line of bodyLines) {
    ctx.fillText(line, contentX, curY);
    curY += bodyLineH;
  }
  curY += metrics.sectionGap * 2;

  // Action buttons
  const btnH = metrics.ctaH;
  const btnW = contentW;

  if (msg.actionType === "accept_decline") {
    const halfW = (contentW - 4) / 2;
    // ACCEPT button
    const acceptRect: Rect = { x: contentX, y: curY, w: halfW, h: btnH };
    ctx.fillStyle = "#0a2a10";
    ctx.fillRect(acceptRect.x, acceptRect.y, acceptRect.w, acceptRect.h);
    ctx.strokeStyle = "#4dff88";
    ctx.lineWidth = 1;
    ctx.strokeRect(acceptRect.x, acceptRect.y, acceptRect.w, acceptRect.h);
    ctx.fillStyle = "#4dff88";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("ACCEPT", acceptRect.x + acceptRect.w / 2, acceptRect.y + acceptRect.h / 2);
    uiRects.messageAction = acceptRect;

    // DECLINE button
    const declineRect: Rect = { x: contentX + halfW + 4, y: curY, w: halfW, h: btnH };
    ctx.fillStyle = "#2a0a0a";
    ctx.fillRect(declineRect.x, declineRect.y, declineRect.w, declineRect.h);
    ctx.strokeStyle = "#ff4444";
    ctx.lineWidth = 1;
    ctx.strokeRect(declineRect.x, declineRect.y, declineRect.w, declineRect.h);
    ctx.fillStyle = "#ff4444";
    ctx.fillText("DECLINE", declineRect.x + declineRect.w / 2, declineRect.y + declineRect.h / 2);
    uiRects.messageActionSecondary = declineRect;
    curY += btnH + 4;
  } else if (msg.actionType === "navigate") {
    const label = "GO TO " + ((msg.actionPayload as Record<string, unknown> | null)?.phase ?? "").toString().toUpperCase();
    const navRect: Rect = { x: contentX, y: curY, w: btnW, h: btnH };
    ctx.fillStyle = "#0a1a2a";
    ctx.fillRect(navRect.x, navRect.y, navRect.w, navRect.h);
    ctx.strokeStyle = "#a78bfa";
    ctx.lineWidth = 1;
    ctx.strokeRect(navRect.x, navRect.y, navRect.w, navRect.h);
    ctx.fillStyle = "#a78bfa";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, navRect.x + navRect.w / 2, navRect.y + navRect.h / 2);
    uiRects.messageAction = navRect;
    curY += btnH + 4;
  } else if (msg.actionType === "claim_reward") {
    const claimed = !!(msg.actionPayload as Record<string, unknown> | null)?.claimed;
    const claimRect: Rect = { x: contentX, y: curY, w: btnW, h: btnH };
    ctx.fillStyle = claimed ? "#1a1a1a" : "#2a0a1a";
    ctx.fillRect(claimRect.x, claimRect.y, claimRect.w, claimRect.h);
    ctx.strokeStyle = claimed ? "#555" : "#ff6b9d";
    ctx.lineWidth = 1;
    ctx.strokeRect(claimRect.x, claimRect.y, claimRect.w, claimRect.h);
    ctx.fillStyle = claimed ? "#555" : "#ff6b9d";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(claimed ? "CLAIMED" : "CLAIM REWARD", claimRect.x + claimRect.w / 2, claimRect.y + claimRect.h / 2);
    if (!claimed) uiRects.messageAction = claimRect;
    curY += btnH + 4;
  } else if (msg.actionType === "external_link") {
    const linkLabel = ((msg.actionPayload as Record<string, unknown> | null)?.label ?? "OPEN LINK").toString().toUpperCase();
    const linkRect: Rect = { x: contentX, y: curY, w: btnW, h: btnH };
    ctx.fillStyle = "#1a1a0a";
    ctx.fillRect(linkRect.x, linkRect.y, linkRect.w, linkRect.h);
    ctx.strokeStyle = "#ff9944";
    ctx.lineWidth = 1;
    ctx.strokeRect(linkRect.x, linkRect.y, linkRect.w, linkRect.h);
    ctx.fillStyle = "#ff9944";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(linkLabel, linkRect.x + linkRect.w / 2, linkRect.y + linkRect.h / 2);
    uiRects.messageAction = linkRect;
    curY += btnH + 4;
  }

  // BACK button (pinned to bottom, matching friends page standard)
  const backY = scrY + scrH - btnH - 6;
  const backRect: Rect = { x: contentX, y: backY, w: btnW, h: btnH };
  ctx.fillStyle = "#162030";
  ctx.fillRect(backRect.x, backRect.y, backRect.w, backRect.h);
  ctx.strokeStyle = "#5a7a9a";
  ctx.lineWidth = 1;
  ctx.strokeRect(backRect.x, backRect.y, backRect.w, backRect.h);
  ctx.fillStyle = "#8a9ab0";
  ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("BACK", backRect.x + backRect.w / 2, backRect.y + backRect.h / 2);
  uiRects.messageBack = backRect;
}
